/**
 * Current Stock Page
 *
 * Page de consultation du stock actuel avec KPIs, filtres, et drawer.
 * @see US-12b.4 - Page Liste Stock
 * @see US-12b.6 - Deep Linking
 * @see Sprint 27 - Stock a Date
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, TrendingDown, Warehouse, Package } from 'lucide-react';
import { KPICard, Skeleton, EmptyState, formatDateParamDisplay, isDateParamToday, dateParamHasTime } from '@/components/ui';
import { useDrawerRoute, useCurrency, useCanSeeFinancials } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useCurrentStock, useStockKPIs } from '../hooks/useCurrentStock';
import { CurrentStockFilters } from '../components/CurrentStockFilters';
import { CurrentStockTable } from '../components/CurrentStockTable';
import { ProductStockDrawer } from '../components/ProductStockDrawer';
import type { CurrentStockItem, QueryCurrentStockDto, MarketType } from '@bstock/shared';

type SortField = QueryCurrentStockDto['sortBy'];
type SortOrder = QueryCurrentStockDto['sortOrder'];

const DEFAULT_LIMIT = 20;

/**
 * Parse URL params into filters (deep linking)
 */
function parseUrlParams(searchParams: URLSearchParams): {
  search: string;
  categoryId: string;
  marketType: string;
  lowStockOnly: boolean;
  page: number;
  sortBy: SortField;
  sortOrder: SortOrder;
  date: string | null;
} {
  return {
    search: searchParams.get('search') ?? '',
    categoryId: searchParams.get('categoryId') ?? '',
    marketType: searchParams.get('marketType') ?? '',
    lowStockOnly: searchParams.get('lowStockOnly') === 'true',
    page: parseInt(searchParams.get('page') ?? '1', 10),
    sortBy: (searchParams.get('sortBy') as SortField) ?? 'name',
    sortOrder: (searchParams.get('sortOrder') as SortOrder) ?? 'asc',
    date: searchParams.get('date') ?? null,
  };
}

export default function CurrentStockPage() {
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Permissions
  const showFinancials = useCanSeeFinancials();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Drawer routing with deep linking
  const drawer = useDrawerRoute('/stock/actuel', { paramName: 'rawProductId' });

  // Filters state from URL
  const [search, setSearch] = useState(urlParams.search);
  const [categoryId, setCategoryId] = useState(urlParams.categoryId);
  const [marketType, setMarketType] = useState(urlParams.marketType);
  const [lowStockOnly, setLowStockOnly] = useState(urlParams.lowStockOnly);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<SortField>(urlParams.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlParams.sortOrder);
  const [date, setDate] = useState<string | null>(urlParams.date);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Query params
  const queryParams = useMemo<QueryCurrentStockDto>(() => ({
    page,
    limit,
    search: search || undefined,
    categoryId: categoryId || undefined,
    marketType: marketType ? (marketType as MarketType) : undefined,
    lowStockOnly,
    sortBy,
    sortOrder,
    date: date || undefined, // Sprint 27
  }), [page, limit, search, categoryId, marketType, lowStockOnly, sortBy, sortOrder, date]);

  // Fetch data
  const { data: stockData, isLoading } = useCurrentStock(queryParams);
  const { data: kpis } = useStockKPIs(date); // Pass date for historical KPIs

  const items = stockData?.data ?? [];
  const pagination = stockData?.pagination ?? {
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  };

  // Handlers
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      setPage(1);
      updateUrl({ search: value || null, page: null });
    },
    [updateUrl]
  );

  const handleCategoryChange = useCallback(
    (value: string) => {
      setCategoryId(value);
      setPage(1);
      updateUrl({ categoryId: value || null, page: null });
    },
    [updateUrl]
  );

  const handleMarketTypeChange = useCallback(
    (value: string) => {
      setMarketType(value);
      setPage(1);
      updateUrl({ marketType: value || null, page: null });
    },
    [updateUrl]
  );

  const handleLowStockOnlyChange = useCallback(
    (value: boolean) => {
      setLowStockOnly(value);
      setPage(1);
      updateUrl({ lowStockOnly: value ? 'true' : null, page: null });
    },
    [updateUrl]
  );

  const handleDateChange = useCallback(
    (value: string | null) => {
      setDate(value);
      setPage(1);
      updateUrl({ date: value || null, page: null });
    },
    [updateUrl]
  );

  const handleSort = useCallback(
    (field: SortField, order: SortOrder) => {
      setSortBy(field);
      setSortOrder(order);
      setPage(1);
      updateUrl({ sortBy: field || null, sortOrder: order || null, page: null });
    },
    [updateUrl]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      updateUrl({ page: String(newPage) });
    },
    [updateUrl]
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      setLimit(newLimit);
      setPage(1);
      updateUrl({ page: null });
    },
    [updateUrl]
  );

  const handleRowClick = useCallback(
    (item: CurrentStockItem) => {
      drawer.open(item.rawProductId);
    },
    [drawer]
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Warehouse className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">
              {isDateParamToday(date) && !dateParamHasTime(date) ? 'Stock actuel' : date ? `Stock au ${formatDateParamDisplay(date)}` : 'Stock actuel'}
            </h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              {!date || (isDateParamToday(date) && !dateParamHasTime(date))
                ? "Consultez l'etat du stock de vos produits bruts"
                : 'Consultation historique du stock'}
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className={`grid grid-cols-2 ${showFinancials ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
          {showFinancials && (
            <KPICard
              title="Valeur totale"
              value={kpis ? formatCentimes(kpis.totalStockValue, currency) : '-'}
              icon={<Warehouse className="w-5 h-5" />}
            />
          )}
          <KPICard
            title="Stock faible"
            value={kpis?.lowStockCount ?? 0}
            icon={<TrendingDown className="w-5 h-5" />}
            badge={kpis && kpis.lowStockCount > 0 ? { label: 'A surveiller', variant: 'warning' } : undefined}
          />
          <KPICard
            title="Stock critique"
            value={kpis?.criticalStockCount ?? 0}
            icon={<AlertTriangle className="w-5 h-5" />}
            badge={kpis && kpis.criticalStockCount > 0 ? { label: 'Urgent', variant: 'danger' } : undefined}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <CurrentStockFilters
          search={search}
          categoryId={categoryId}
          marketType={marketType}
          lowStockOnly={lowStockOnly}
          date={date}
          onSearchChange={handleSearchChange}
          onCategoryChange={handleCategoryChange}
          onMarketTypeChange={handleMarketTypeChange}
          onLowStockOnlyChange={handleLowStockOnlyChange}
          onDateChange={handleDateChange}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Package}
            title="Aucun produit trouve"
            description="Aucun produit ne correspond a vos criteres de recherche"
          />
        ) : (
          <CurrentStockTable
            items={items}
            pagination={pagination}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            showFinancials={showFinancials}
          />
        )}
      </div>

      {/* Product Stock Drawer */}
      <ProductStockDrawer
        rawProductId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={(open) => !open && drawer.close()}
        showFinancials={showFinancials}
        date={date}
      />
    </div>
  );
}
